/**
 * 무통장 입금 계좌 마스터 — BO 계좌 관리(M-4, [ID 52] 15차 D-1) FE 타입.
 * 근거: everline-qa-20260605-merge-spec.md M-4 ①, qa-52(deposits-admin.png).
 * be-order 동결 계약(order-service admin CRUD, `/admin/v1/orders/bank-accounts`).
 *
 * 운영자가 무통장(직접입금) 안내 계좌를 등록/수정/삭제한다. 각 계좌는 노출상점
 * (국문/영문/중문/일문 다중)·통화(KRW/USD/CNY/JPY 단일)·SWIFT·지점명을 갖고,
 * SF 결제 단계에서 접속 상점/통화에 맞는 계좌만 필터 노출된다(M-4 ④).
 */

/**
 * 노출상점 키 — BCP-47(storefront 로케일과 동일). M-4 ① "국문/영문/중문/일문".
 * 다중선택(한 계좌가 여러 몰에 노출될 수 있음).
 */
export const EXPOSED_MALLS = ['ko-KR', 'en-US', 'zh-CN', 'ja-JP'] as const;
export type ExposedMall = (typeof EXPOSED_MALLS)[number];

/** 노출상점 라벨(BO 한국어 전용). */
export const EXPOSED_MALL_LABELS: Record<ExposedMall, string> = {
  'ko-KR': '국문몰',
  'en-US': '영문몰',
  'zh-CN': '중문몰',
  'ja-JP': '일문몰',
};

/**
 * 통화 — M-4 ① "KRW/USD/CNY/JPY". 계좌당 단일. 해외 송금용 SWIFT 와 함께
 * 결제 단계에서 접속 상점·통화에 맞는 계좌만 노출하는 필터 키(M-4 ④).
 */
export const CURRENCIES = ['KRW', 'USD', 'CNY', 'JPY'] as const;
export type Currency = (typeof CURRENCIES)[number];

export const CURRENCY_LABELS: Record<Currency, string> = {
  KRW: 'KRW (원)',
  USD: 'USD (달러)',
  CNY: 'CNY (위안)',
  JPY: 'JPY (엔)',
};

/**
 * BankAccountResponse — be-order 동결 계약. 목록(data.accounts[])·단건·생성·수정
 * 응답 모두 이 형태(전체 필드). = UpsertRequest 필드 + bankAccountId.
 */
export interface BankAccountDetail {
  bankAccountId: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  enabled: boolean;
  /** 노출상점(BCP-47 다중). 빈 배열이면 어느 몰에도 노출되지 않음. */
  exposedMalls: ExposedMall[];
  currency: Currency;
  /** 해외 송금용. 선택 입력. */
  swiftCode?: string | null;
  branchName?: string | null;
  sortOrder: number;
}

/**
 * 목록 행 = 단건 응답과 동일 형태(be-order 가 목록에 전체 필드를 내려준다).
 * 별도 요약 타입을 두지 않고 Detail 을 그대로 목록 행으로 쓴다.
 */
export type BankAccountSummary = BankAccountDetail;

/** POST/PUT body. bankAccountId 제외. PUT=전체교체(be-order 계약). */
export interface BankAccountPayload {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  enabled: boolean;
  exposedMalls: ExposedMall[];
  currency: Currency;
  swiftCode?: string | null;
  branchName?: string | null;
  sortOrder: number;
}

/** 신규 계좌 기본값(사용 ON·국문몰·KRW·SWIFT/지점명 미입력). */
export const DEFAULT_BANK_ACCOUNT_PAYLOAD: BankAccountPayload = {
  bankName: '',
  accountNumber: '',
  accountHolder: '',
  enabled: true,
  exposedMalls: ['ko-KR'],
  currency: 'KRW',
  swiftCode: null,
  branchName: null,
  sortOrder: 0,
};
